export type PersistedAssistantMessage = {
  role: 'user' | 'assistant';
  content: string;
  id?: number;
  feedback?: 'good' | 'bad' | null;
};

export type PersistedAssistantConversation = {
  conversation_id: number;
  messages: PersistedAssistantMessage[];
};

type RecoveryOptions = {
  conversationId: number | null;
  conversationPath: (conversationId: number) => string;
  userMessage: string;
  knownMessageIds?: number[];
  delaysMs?: number[];
  fetcher?: typeof fetch;
};

/** Return the persisted reply that follows the most recent matching user turn. */
export function findPersistedReply(
  messages: PersistedAssistantMessage[],
  userMessage: string,
  knownMessageIds: readonly number[] = [],
): PersistedAssistantMessage | null {
  const knownIds = new Set(knownMessageIds);
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index];
    if (message.role !== 'user' || message.content !== userMessage) continue;

    for (let next = index + 1; next < messages.length; next += 1) {
      const candidate = messages[next];
      // A later user turn ends the search: a reply beyond it belongs to that
      // turn (e.g. the conversation advanced in another tab), so treating it
      // as this turn's reply would fabricate an answer that was never given.
      if (candidate.role === 'user') return null;
      if (candidate.role === 'assistant'
        && candidate.content.trim().length > 0
        && candidate.id != null
        && !knownIds.has(candidate.id)) return candidate;
    }
    return null;
  }
  return null;
}

/**
 * A broken browser-to-server stream does not necessarily stop the server turn.
 * Give the backend time to save it, then reconcile from persisted history.
 */
export async function recoverPersistedAssistantTurn({
  conversationId,
  conversationPath,
  userMessage,
  knownMessageIds = [],
  delaysMs = [1_000, 2_000, 3_000],
  fetcher = fetch,
}: RecoveryOptions): Promise<PersistedAssistantConversation | null> {
  if (!conversationId) return null;

  for (const delayMs of delaysMs) {
    await new Promise((resolve) => setTimeout(resolve, delayMs));
    try {
      const response = await fetcher(conversationPath(conversationId), { cache: 'no-store' });
      if (!response.ok) continue;
      const conversation = await response.json() as PersistedAssistantConversation;
      if (!Array.isArray(conversation.messages)) continue;
      if (findPersistedReply(conversation.messages, userMessage, knownMessageIds)) return conversation;
    } catch {
      // The recovery fetch is best-effort; keep polling until the budget expires.
    }
  }

  return null;
}
